import { Dropdown, MenuProps } from 'antd';
import React, { ComponentType, FC, memo, ReactNode, useState } from 'react';
import { WarehouseMapTool } from '../../model/types';
import { MenuInfo } from 'rc-menu/lib/interface';

interface ToolProps {
  id: WarehouseMapTool;
  Icon: ComponentType<{ className?: string }>;
  label?: string;
  activeTool: WarehouseMapTool;
  onClick: (id: WarehouseMapTool) => void;
  multiTools?: Array<{
    id: WarehouseMapTool;
    Icon: ComponentType<{ className?: string }>;
    label?: string;
  }>;
  disabledTools?: ReadonlySet<WarehouseMapTool>;
  disabledTitle?: string;
}

const activeIconClass = 'text-white [&>path]:stroke-white [&>rect]:stroke-white [&>ellipse]:stroke-white';
const inactiveIconClass = 'text-primary [&>path]:stroke-primary [&>rect]:stroke-primary [&>ellipse]:stroke-primary';

const renderItem = (Icon: ComponentType<{ className?: string }>, label: string | undefined, isActive: boolean): ReactNode => (
  <div className="flex items-center gap-2.5 min-w-[110px]">
    <Icon className={isActive ? activeIconClass : inactiveIconClass} />
    {label && <span className={`text-sm leading-none ${isActive ? 'text-white' : 'text-primaryLight'}`}>{label}</span>}
  </div>
);

export const Tool: FC<ToolProps> = memo((props) => {
  const { id, Icon, label, activeTool, onClick, multiTools, disabledTools, disabledTitle } = props;

  const [activeMultiToolKey, setActiveMultiToolKey] = useState([id]);

  const isMultiTool = Boolean(multiTools?.length);

  const isToolDisabled = (toolId: WarehouseMapTool): boolean => Boolean(disabledTools?.has(toolId));

  const headDisabled = isToolDisabled(activeMultiToolKey[0] ?? id);

  const handleClick = (): void => {
    if (isToolDisabled(id)) return;
    onClick(id);
  };

  const selectMultiToolItem = ({ key }: MenuInfo): void => {
    const nextKey = key as WarehouseMapTool;
    if (isToolDisabled(nextKey)) return;
    onClick(nextKey);
    setActiveMultiToolKey([nextKey]);
  };

  const multiToolItems: MenuProps['items'] = multiTools
    ? [
        {
          key: id,
          disabled: isToolDisabled(id),
          label: renderItem(Icon, label, activeTool === id),
          className: activeTool === id ? '!bg-accent' : '!bg-none',
          title: isToolDisabled(id) ? disabledTitle : undefined,
        },
        ...multiTools.map(({ id: subId, Icon: SubIcon, label: subLabel }) => ({
          key: subId,
          disabled: isToolDisabled(subId),
          label: renderItem(SubIcon, subLabel, activeTool === subId),
          className: activeTool === subId ? '!bg-accent' : '!bg-none',
          title: isToolDisabled(subId) ? disabledTitle : undefined,
        })),
      ]
    : [];

  const activeMultiToolItem = multiToolItems.find((tool) => tool?.key === activeMultiToolKey?.[0]);

  // Resolve the Icon component for the currently active sub-tool (used in the toolbar head button).
  const allSubTools = [{ id, Icon, label }, ...(multiTools ?? [])];
  const activeHeadTool = allSubTools.find((t) => t.id === (activeMultiToolKey[0] ?? id));
  const HeadIcon = activeHeadTool?.Icon ?? Icon;
  const isHeadActive = activeTool === (activeMultiToolItem?.key ?? id);

  const headDisabledClasses = headDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer';
  const singleDisabled = isToolDisabled(id);
  const singleDisabledClasses = singleDisabled ? 'opacity-40 cursor-not-allowed pointer-events-none' : 'cursor-pointer';

  const menu = (): ReactNode => {
    return (
      <Dropdown
        menu={{
          items: multiToolItems,
          selectable: true,
          defaultSelectedKeys: activeMultiToolKey,
          onClick: selectMultiToolItem,
        }}
        trigger={['click']}
        placement="bottom"
      >
        {/* Toolbar head button shows only the icon — label appears only inside the dropdown */}
        <div
          className={`flex items-center p-2 ${isHeadActive ? 'bg-accent' : 'bg-none'} rounded-lg ${headDisabledClasses} transition`}
          title={headDisabled ? disabledTitle : undefined}
        >
          <HeadIcon className={isHeadActive ? activeIconClass : inactiveIconClass} />
        </div>
      </Dropdown>
    );
  };

  return isMultiTool ? (
    <>{menu()}</>
  ) : (
    <div
      className={`flex items-center space-x-3.5 p-2 ${
        activeTool === id ? 'bg-accent' : 'bg-none'
      } rounded-lg ${singleDisabledClasses} transition`}
      onClick={handleClick}
      title={singleDisabled ? disabledTitle : undefined}
    >
      <Icon className={activeTool === id ? activeIconClass : inactiveIconClass} />
    </div>
  );
});
